import React, { useState, useEffect } from 'react';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { cn } from '@/utils/cn';

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) {
        setSidebarCollapsed(false);
        setShowMobileSidebar(false);
      }
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  const toggleSidebar = () => {
    if (isMobile) {
      setShowMobileSidebar(!showMobileSidebar);
    } else {
      setSidebarCollapsed(!sidebarCollapsed);
    }
  };

  const closeMobileSidebar = () => {
    if (isMobile) {
      setShowMobileSidebar(false);
    }
  };

  return (
    <div className="h-screen flex bg-gray-50">
      {/* Mobile overlay */}
      {isMobile && showMobileSidebar && (
        <div
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75"
          onClick={closeMobileSidebar}
        />
      )}

      {/* Sidebar */}
      <div className={cn(
        'fixed inset-y-0 left-0 z-50 transition-all duration-300',
        isMobile ? (
          showMobileSidebar ? 'w-64' : '-translate-x-full w-64'
        ) : (
          sidebarCollapsed ? 'w-16' : 'w-64'
        )
      )}>
        <Sidebar 
          isCollapsed={!isMobile && sidebarCollapsed} 
          onNavigate={closeMobileSidebar}
        />
      </div>

      {/* Main content */}
      <div className={cn(
        'flex-1 flex flex-col transition-all duration-300',
        isMobile ? 'ml-0' : (sidebarCollapsed ? 'ml-16' : 'ml-64')
      )}>
        {/* Top bar */}
        <TopBar onToggleSidebar={toggleSidebar} />

        {/* Page content */}
        <main className="flex-1 overflow-hidden">
          <div className="h-full overflow-y-auto">
            {children}
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-primary-100 px-4 sm:px-6 py-3">
          <div className="text-center text-sm text-primary-800">
            <span>© 2025 </span>
            <a
              href="https://bearsgroupsolutions.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-800 hover:text-primary-900 font-medium underline"
            >
              Bearsgroupsolutions.com
            </a>
          </div>
        </footer>
      </div>
    </div>
  );
};